// src/components/CheckoutPage.jsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useCart } from "../context/CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import BackButton from "./BackButton";
import {
  MapPin,
  CreditCard,
  Lock,
  Truck,
  Shield,
  ShoppingBag,
  Home,
  Check,
} from "lucide-react";
import { CircularProgress, Snackbar, Alert } from "@mui/material";
import { getUserProfile } from "../utils/auth";
import { placeOrder } from "../api/checkoutApi";
import paypalLogo from "../images/PayPal.svg.webp";
import applePayLogo from "../images/Applepay.png";
import {
  mapBillingToCheckoutFields,
  loadCheckoutPrefill,
  clearCheckoutPrefill,
  DEFAULT_CHECKOUT_PHONE,
} from "../utils/checkoutPrefill";
import { trackCheckoutStep } from "../services/uiAgentSync";
import {
  normalizeAgentLines,
  normalizeAgentTotals,
  DEFAULT_AGENT_TAX_PERCENT,
} from "../utils/agentBillingPayload";

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

function AgentLinePrice({ item, formatPrice }) {
  const lineTotal =
    item.lineTotal != null
      ? item.lineTotal
      : (parseFloat(item.price) || 0) * (item.quantity ?? 1);

  if (item.priceMatched && item.originalLineTotal != null) {
    return (
      <div className="flex flex-col items-end">
        <span className="text-xs text-gray-400 line-through">
          ${formatPrice(item.originalLineTotal)}
        </span>
        <span className="text-sm font-semibold text-green-600">
          ${formatPrice(lineTotal)}
        </span>
      </div>
    );
  }

  return (
    <p className="text-sm font-semibold text-gray-800">
      ${formatPrice(lineTotal)}
    </p>
  );
}

export default function CheckoutPage() {
  const { cartItems, totalPrice, clearCart, replaceCartItemsFromAgent } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = getUserProfile();

  // Agent checkout: hydrate UI cart from navigate state if context is still empty
  // (setState from onLastResponse may not have flushed before navigation).
  useEffect(() => {
    const cartData = location.state?.cartData;
    if (Array.isArray(cartData) && cartData.length > 0 && cartItems.length === 0) {
      replaceCartItemsFromAgent(cartData);
    }
  }, [location.state, cartItems.length, replaceCartItemsFromAgent]);

  // UI → Agent sync: single-page checkout — report entry as billing_review
  useEffect(() => {
    trackCheckoutStep("billing_review");
  }, []);

  const [shippingInfo, setShippingInfo] = useState({
    firstName: userProfile?.customer_name?.split(" ")[0] || "",
    lastName: userProfile?.customer_name?.split(" ").slice(1).join(" ") || "",
    email: userProfile?.email || "",
    phone: userProfile?.phone || DEFAULT_CHECKOUT_PHONE,
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });

  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    cardholderName: "",
    expirationDate: "",
    cvv: "",
  });

  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    firstName: "",
    lastName: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const applyBillingPrefill = useCallback((billing) => {
    const mapped = mapBillingToCheckoutFields(billing);
    if (!mapped) return;

    setShippingInfo((prev) => ({ ...prev, ...mapped.shipping }));
    setBillingAddress((prev) => ({ ...prev, ...mapped.billing }));
    setSameAsShipping(true);
    if (mapped.card?.cardNumber || mapped.card?.cardholderName) {
      setCardDetails((prev) => ({ ...prev, ...mapped.card }));
      setPaymentMethod("card");
    }
  }, []);

  useEffect(() => {
    const billingFromNav = location.state?.billingDetails;
    const billingFromAgent = billingFromNav || loadCheckoutPrefill();
    if (billingFromAgent) {
      applyBillingPrefill(billingFromAgent);
    }
  }, [location.state, applyBillingPrefill]);

  const isAgentCheckout = Boolean(
    location.state?.isAgentCheckout ||
      (location.state?.fromChat &&
        (Array.isArray(location.state?.lines) ||
          location.state?.totals != null))
  );

  const agentLines = useMemo(
    () => normalizeAgentLines(location.state?.lines),
    [location.state?.lines]
  );

  const agentTotals = useMemo(
    
    () => normalizeAgentTotals(location.state?.totals),
    [location.state?.totals]
  );

  const summaryItems = agentLines.length > 0 ? agentLines : cartItems;

  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Bill: agent totals from backend when present; otherwise cart + 5% tax + optional promo.
  const subtotal = agentTotals ? agentTotals.originalSubtotal : totalPrice;
  
  const discount = agentTotals
    ? agentTotals.discountTotal
    : promoApplied
      ? subtotal * 0.1
      : 0;
  const taxableSubtotal = Math.max(0, subtotal - discount);
  const taxPercent = agentTotals
    ? agentTotals.taxPercent || DEFAULT_AGENT_TAX_PERCENT
    : DEFAULT_AGENT_TAX_PERCENT;
  const tax = agentTotals
    ? agentTotals.tax
    : roundMoney(taxableSubtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
  const total = agentTotals
    ? agentTotals.total
    : roundMoney(taxableSubtotal + tax);

  const showToast = (message, severity = "error") => {
    setToast({ open: true, message, severity });
  };

  const handleInputChange = (section, field, value) => {
    if (section === "shipping") {
      setShippingInfo((prev) => ({ ...prev, [field]: value }));
      if (sameAsShipping) {
        setBillingAddress((prev) => ({ ...prev, [field]: value }));
      }
    } else if (section === "card") {
      setCardDetails((prev) => ({ ...prev, [field]: value }));
    } else if (section === "billing") {
      setBillingAddress((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSameAsShippingChange = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setBillingAddress({
        firstName: shippingInfo.firstName,
        lastName: shippingInfo.lastName,
        streetAddress: shippingInfo.streetAddress,
        city: shippingInfo.city,
        state: shippingInfo.state,
        zipCode: shippingInfo.zipCode,
        country: shippingInfo.country,
      });
    }
  };

  const handleApplyPromo = () => {
    if (promoCode.trim().toUpperCase() === "SAVE10") {
      setPromoApplied(true);
      showToast("Promo code applied! 10% discount.", "success");
    } else {
      setPromoApplied(false);
      showToast("Invalid promo code.");
    }
  };

  const validateForm = () => {
    const shippingRequired = [
      ["firstName", "First name"],
      ["lastName", "Last name"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["streetAddress", "Street address"],
      ["city", "City"],
      ["state", "State"],
      ["zipCode", "ZIP code"],
    ];
    for (const [field, label] of shippingRequired) {
      if (!shippingInfo[field]?.trim()) return `${label} is required`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingInfo.email)) {
      return "Please enter a valid email address";
    }
    if (paymentMethod === "card") {
      if (!cardDetails.cardNumber?.trim()) return "Card number is required";
      if (!cardDetails.cardholderName?.trim()) return "Cardholder name is required";
      if (!cardDetails.expirationDate?.trim()) return "Expiration date is required";
      if (!cardDetails.cvv?.trim()) return "CVV is required";
    }
    if (!sameAsShipping) {
      const billingRequired = [
        ["firstName", "Billing first name"],
        ["lastName", "Billing last name"],
        ["streetAddress", "Billing street address"],
        ["city", "Billing city"],
        ["state", "Billing state"],
        ["zipCode", "Billing ZIP code"],
      ];
      for (const [field, label] of billingRequired) {
        if (!billingAddress[field]?.trim()) return `${label} is required`;
      }
    }
    return null;
  };

  const handlePlaceOrder = async () => {
    const validationError = validateForm();
    if (validationError) {
      showToast(validationError);
      return;
    }

    if (!userProfile?.customer_id) {
      showToast("Please log in to place an order");
      return;
    }

    const effectiveBilling = sameAsShipping ? shippingInfo : billingAddress;

    const orderPayload = {
      customer_id: userProfile.customer_id,
      shipping_address: {
        first_name: shippingInfo.firstName,
        last_name: shippingInfo.lastName,
        email: shippingInfo.email,
        phone: shippingInfo.phone || DEFAULT_CHECKOUT_PHONE,
        street_address: shippingInfo.streetAddress,
        city: shippingInfo.city,
        state: shippingInfo.state,
        zip_code: shippingInfo.zipCode,
        country: shippingInfo.country,
      },
      billing_address: {
        first_name: effectiveBilling.firstName,
        last_name: effectiveBilling.lastName,
        street_address: effectiveBilling.streetAddress,
        city: effectiveBilling.city,
        state: effectiveBilling.state,
        zip_code: effectiveBilling.zipCode,
        country: effectiveBilling.country,
      },
      payment_method: paymentMethod,
      ...(paymentMethod === "card" && {
        card_last_four: cardDetails.cardNumber.replace(/\s/g, "").slice(-4),
      }),
      items: cartItems.map((item) => ({
        article_id: String(item.id),
        name: item.name || item.title || "",
        quantity: item.quantity,
        price: item.price,
        size: item.size || null,
        image: item.image_uri || (typeof item.image === "string" && item.image ? item.image : null),
      })),
      subtotal,
      tax,
      shipping: 0,
      discount,
      total,
      promo_code: promoApplied ? promoCode.toUpperCase() : null,
    };

    setLoading(true);
    try {
      const orderResponse = await placeOrder(orderPayload);
      clearCheckoutPrefill();
      showToast("Order placed successfully!", "success");
      // Snapshot cart before clearing for confirmation page
      const orderedItems = [...cartItems];
      try {
        await clearCart();
      } catch (clearErr) {
        console.warn("[Checkout] clearCart after order failed:", clearErr);
      }
      setTimeout(() => {
        navigate("/order-confirmation", {
          state: {
            orderResponse,
            shippingInfo,
            billingAddress: effectiveBilling,
            paymentMethod,
            cardLastFour:
              paymentMethod === "card"
                ? cardDetails.cardNumber.replace(/\s/g, "").slice(-4)
                : null,
            subtotal,
            shipping: 0,
            tax,
            discount,
            total,
            orderedItems,
          },
        });
      }, 600);
    } catch (error) {
      showToast(error.message || "Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-semibold text-gray-800">Your cart is empty</h2>
        <button
          onClick={() => navigate("/cart")}
          className="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
        >
          Go to Cart
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <BackButton />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Security header */}
      <div className="flex flex-wrap items-center justify-center gap-6 mb-8 py-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Secure Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-gray-700">Free Shipping</span>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-600" />
          <span className="text-sm font-medium text-gray-700">SSL Encrypted</span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Column */}
        <div className="flex-1 space-y-6">
          {/* Shipping Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Shipping Information</h2>
                <p className="text-sm text-gray-600">Where should we deliver your order?</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "First Name", field: "firstName", type: "text" },
                { label: "Last Name", field: "lastName", type: "text" },
                { label: "Email", field: "email", type: "email" },
                { label: "Phone", field: "phone", type: "tel" },
              ].map(({ label, field, type }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type={type}
                    value={shippingInfo[field]}
                    onChange={(e) => handleInputChange("shipping", field, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={shippingInfo.streetAddress}
                  onChange={(e) => handleInputChange("shipping", "streetAddress", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
              </div>
              {[
                { label: "City", field: "city" },
                { label: "State", field: "state" },
                { label: "ZIP Code", field: "zipCode" },
              ].map(({ label, field }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={shippingInfo[field]}
                    onChange={(e) => handleInputChange("shipping", field, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={shippingInfo.country}
                  onChange={(e) => handleInputChange("shipping", "country", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Payment Method</h2>
                <p className="text-sm text-gray-600">Select your preferred payment method</p>
              </div>
            </div>

            <div className="flex gap-3 mb-6">
              {[
                { id: "card", label: "Credit Card", icon: <CreditCard className="w-5 h-5" /> },
                {
                  id: "paypal",
                  label: "PayPal",
                  icon: <img src={paypalLogo} alt="PayPal" className="h-5 w-auto object-contain" />,
                },
                {
                  id: "applepay",
                  label: "Apple Pay",
                  icon: <img src={applePayLogo} alt="Apple Pay" className="h-5 w-auto object-contain" />,
                },
              ].map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => setPaymentMethod(id)}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    paymentMethod === id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center gap-1">
                    {icon}
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                </button>
              ))}
            </div>

            {paymentMethod === "paypal" && (
              <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                <div className="flex justify-center">
                  <img src={paypalLogo} alt="PayPal" className="h-20 w-auto object-contain" />
                </div>
                <p className="text-center text-gray-700 text-sm">
                  You'll be securely redirected to PayPal to complete your purchase
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Secure PayPal checkout</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Lock className="w-4 h-4 text-blue-600" />
                    <span>Your payment info is never shared</span>
                  </div>
                </div>
                <button
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      <img src={paypalLogo} alt="PayPal" className="h-5 w-auto object-contain brightness-0 invert" />
                      <span>Continue with PayPal</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {paymentMethod === "applepay" && (
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="flex justify-center">
                  <img src={applePayLogo} alt="Apple Pay" className="h-20 w-auto object-contain" />
                </div>
                <p className="text-center text-gray-700 font-medium">Pay securely with Apple Pay</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Touch ID or Face ID authentication</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Lock className="w-4 h-4 text-gray-700" />
                    <span>End-to-end encrypted</span>
                  </div>
                </div>
                <button
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="w-full py-3 bg-black hover:bg-gray-800 disabled:opacity-60 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      <img src={applePayLogo} alt="Apple Pay" className="h-5 w-auto object-contain" />
                      <span>Pay with Apple Pay</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {paymentMethod === "card" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Card Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cardDetails.cardNumber}
                    onChange={(e) => handleInputChange("card", "cardNumber", e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cardholder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cardDetails.cardholderName}
                    onChange={(e) => handleInputChange("card", "cardholderName", e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={cardDetails.expirationDate}
                      onChange={(e) => handleInputChange("card", "expirationDate", e.target.value)}
                      placeholder="MM/YY"
                      maxLength={5}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CVV <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={cardDetails.cvv}
                      onChange={(e) => handleInputChange("card", "cvv", e.target.value)}
                      placeholder="•••"
                      maxLength={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Billing Address */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Home className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Billing Address</h2>
                <p className="text-sm text-gray-600">Where should we send the invoice?</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsShipping}
                  onChange={(e) => handleSameAsShippingChange(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Same as shipping address</span>
              </label>
            </div>

            {!sameAsShipping && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: "First Name", field: "firstName" },
                  { label: "Last Name", field: "lastName" },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={billingAddress[field]}
                      onChange={(e) => handleInputChange("billing", field, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billingAddress.streetAddress}
                    onChange={(e) => handleInputChange("billing", "streetAddress", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                {[
                  { label: "City", field: "city" },
                  { label: "State", field: "state" },
                  { label: "ZIP Code", field: "zipCode" },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={billingAddress[field]}
                      onChange={(e) => handleInputChange("billing", field, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={billingAddress.country}
                    onChange={(e) => handleInputChange("billing", "country", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Order Summary */}
        <div className="w-full lg:w-96">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Order Summary</h2>

            {/* Cart Items */}
            <div className="mb-6 space-y-3 max-h-64 overflow-y-auto">
              {summaryItems.map((item, index) => (
                <div
                  key={item.cartLineUid || item.id || item.product_id || index}
                  className="flex items-center gap-3"
                >
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    {(item.image_uri || item.image) ? (
                      <img
                        src={item.image_uri || (Array.isArray(item.image) ? item.image[0] : item.image)}
                        alt={item.title || item.name}
                        className="w-full h-full object-cover rounded"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <ShoppingBag className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {item.title || item.name || "Item"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.size ? `Size: ${item.size} · ` : ""}Qty: {item.quantity}
                    </p>
                  </div>
                  <AgentLinePrice item={item} formatPrice={formatPrice} />
                </div>
              ))}
            </div>

            {/* Promo Code — manual checkout only */}
            {!isAgentCheckout && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Promo Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoApplied(false);
                  }}
                  placeholder="Enter code (e.g. SAVE10)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || promoApplied}
                  className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {promoApplied ? "Applied" : "Apply"}
                </button>
              </div>
              {promoApplied && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> 10% discount applied
                </p>
              )}
            </div>
            )}

            {/* Price Breakdown */}
            <div className="border-t border-gray-200 pt-4 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Tax ({taxPercent}%)
                </span>
                <span className="font-medium">${formatPrice(tax)}</span>
              </div>
              {!isAgentCheckout && discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Price-Match Adjustment</span>
                  <span className="font-medium text-green-600">-${formatPrice(discount)}</span>
                </div>
              )}
              {isAgentCheckout && discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Price-Match Adjustment</span>
                  <span className="font-medium text-green-600">-${formatPrice(discount)}</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <span className="text-2xl font-bold text-blue-600">${formatPrice(total)}</span>
              </div>
            </div>

            {paymentMethod === "card" && (
              <button
                onClick={handlePlaceOrder}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-60 transition-all shadow-lg flex items-center justify-center gap-2 mb-4"
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Place Order
                  </>
                )}
              </button>
            )}

            <p className="text-xs text-gray-500 text-center mb-6">
              By placing your order, you agree to our terms and conditions.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800 text-sm">Secure Checkout</span>
              </div>
              <p className="text-xs text-green-700">Your payment information is safe and encrypted.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
